import {Async} from 'Core/models/Async';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {ap as apOption, fold as foldOption, none, Option, some} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {UserUploadFileTypeId} from 'Shared/constants/UserUploadFileType';

export class EmptyUserFile {
  public readonly key = 'EmptyUserFile';

  public readonly file: Option<File> = none;
  public readonly fileTypeId: Option<UserUploadFileTypeId> = none;
}

export class InvalidUserFile {
  public readonly key = 'InvalidUserFile';
  public readonly validationError = 'File must have a valid file and upload type Id.';

  constructor(public readonly file: Option<File>, public readonly fileTypeId: Option<UserUploadFileTypeId>) {}
}

export class FileWithUnsupportedFormat {
  public readonly errorMessage = 'The provided file was of an incorrect format. Please upload a .CSV file.';
  public readonly key = 'FileWithUnsupportedFormat';

  constructor(public readonly file: Option<File>, public readonly fileTypeId: Option<UserUploadFileTypeId>) {}
}

export class ValidUserFile {
  public static create(file: Option<File>, fileTypeId: Option<UserUploadFileTypeId>) {
    const newValidUserFile = (f: File) => (fT: UserUploadFileTypeId) => new ValidUserFile(f, fT);

    return pipe(
      some(newValidUserFile),
      apOption(file),
      apOption(fileTypeId),
      foldOption(
        () => new InvalidUserFile(file, fileTypeId) as UserFile,
        validFile => {
          return validFile.file.name.toUpperCase().includes('XLS') || validFile.file.name.toUpperCase().includes('XLSX')
            ? validFile
            : new FileWithUnsupportedFormat(file, fileTypeId);
        }
      )
    );
  }

  public readonly key = 'ValidUserFile';
  private constructor(public readonly file: File, public readonly fileTypeId: UserUploadFileTypeId) {}
}

export type UserFileResults = {
  successCount: number;
  failureCount: number;
  errors: string[];
};

export class CompletedFileUpload {
  public readonly key = 'CompletedUserFile';

  constructor(public readonly results: Async<UserFileResults>) {}
}

export type UserFileUpload = Async<CompletedFileUpload>;

export type UserFile =
  | ValidUserFile
  | InvalidUserFile
  | EmptyUserFile
  | FileWithUnsupportedFormat
  | CompletedFileUpload;

export type UserFileOptions<TReturn> = ToMatchOptions<UserFile, 'key', TReturn>;

export const matchUserFile = <TReturn>(userFile: UserFile, options: UserFileOptions<TReturn>): TReturn => {
  switch (userFile.key) {
    case 'EmptyUserFile':
      return options.EmptyUserFile(userFile);
    case 'InvalidUserFile':
      return options.InvalidUserFile(userFile);
    case 'FileWithUnsupportedFormat':
      return options.FileWithUnsupportedFormat(userFile);
    case 'CompletedUserFile':
      return options.CompletedUserFile(userFile);
    case 'ValidUserFile':
      return options.ValidUserFile(userFile);
  }
};
